import LibrarySyncStatus from './components/LibrarySyncStatus';
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { createGlobalStyle } from 'styled-components';
import { HelmetProvider } from 'react-helmet-async';
import SEO from './components/SEO';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { LayoutProvider } from './context/LayoutContext';
import { ShelfProvider } from './context/ShelfContext';
import { ShelfFilterProvider } from './context/ShelfFilterContext';
import { WishlistProvider } from './context/WishlistContext';
import { WishlistFilterProvider } from './context/WishlistFilterContext';
import { RatingProvider } from './context/RatingContext';
import { UserProvider } from './context/UserContext';
import { NotificationProvider } from './context/NotificationContext';
import { FriendsProvider } from './context/FriendsContext';
import { FeedProvider } from './context/FeedContext';
import { SearchSessionProvider } from './context/SearchSessionContext';
import { CommentsProvider } from './context/CommentsContext';
import { MessagesProvider } from './context/MessagesContext';
import { AchievementsProvider } from './context/AchievementsContext';
import { CollectionProvider } from './context/CollectionContext';
import { ShelfTabProvider } from './context/ShelfTabContext';
import { Layout } from './components/Layout';
import VkAuthHandler from './components/VkAuthHandler';
import TelegramAuthGate from './components/TelegramAuthGate';
import TelegramBridge from './components/TelegramBridge';
import AppLoader from './components/AppLoader';
import AnalyticsTracker from './components/AnalyticsTracker';
const Feed = lazy(() => import('./pages/Feed'));
const Search = lazy(() => import('./pages/Search'));
const Shelf = lazy(() => import('./pages/Shelf'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Collections = lazy(() => import('./pages/Collections'));
const Fandoms = lazy(() => import('./pages/Fandoms'));
const FandomEditor = lazy(() => import('./pages/FandomEditor'));
const Profile = lazy(() => import('./pages/Profile'));
const Friends = lazy(() => import('./pages/Friends'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ItemDetailPage = lazy(() => import('./pages/ItemDetailPage'));
const ItemDetailRoute = lazy(() => import('./pages/ItemDetailRoute'));
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const VerifyEmail = lazy(() => import('./pages/Auth/VerifyEmail'));
const AdminLogin = lazy(() => import('./pages/Admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const AeroConnect = lazy(() => import('./pages/AeroConnect'));
const SharedCollections = lazy(() => import('./pages/SharedCollections'));
const SharedCollectionDetail = lazy(() => import('./pages/SharedCollectionDetail'));
const ActorPage = lazy(() => import('./pages/ActorPage'));
const DirectorPage = lazy(() => import('./pages/DirectorPage'));
const CompanyPage = lazy(() => import('./pages/CompanyPage'));
const CollectionPage = lazy(() => import('./pages/CollectionPage'));
const PersonalCollectionPage = lazy(() => import('./pages/PersonalCollectionPage'));
const LegalDocPage = lazy(() => import('./pages/LegalDocPage'));
const FandomPage = lazy(() => import('./components/Fandom/FandomPage'));
import ProtectedRoute from './components/ProtectedRoute';
import MobileAdPopup from './components/MobileAdPopup/MobileAdPopup';

/* ─── Глобальные стили с запретом горизонтального скролла ─── */
const GlobalStyles = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body, #root {
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    min-height: 100vh;
  }

  html[data-telegram='true'],
  html[data-telegram='true'] body,
  html[data-telegram='true'] #root {
    height: 100%;
    min-height: 0;
    overflow: hidden;
    overscroll-behavior: none;
  }

  html[data-telegram='true'] [data-polka-layer] {
    top: calc(var(--polka-visible-top, 0px) + var(--polka-safe-top, 0px));
    bottom: auto;
    height: calc(var(--polka-visible-height, var(--polka-viewport-height, 100dvh)) - var(--polka-safe-top, 0px) - var(--polka-safe-bottom, 0px));
    max-height: calc(var(--polka-visible-height, var(--polka-viewport-height, 100dvh)) - var(--polka-safe-top, 0px) - var(--polka-safe-bottom, 0px));
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 12px;
  }
  html[data-telegram='true'] [data-polka-layer] > * { max-height: 100%; min-height: 0; }
  html[data-telegram='true'] input,
  html[data-telegram='true'] textarea { scroll-margin: 16px; font-size: 16px; }

  a {
    color: inherit;
    text-decoration: none;
  }

  button {
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  ::-webkit-scrollbar {
    display: none;
  }

  * {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

function AppContent() {
  const theme = useTheme();

  return (
    <StyledThemeProvider theme={theme}>
      <GlobalStyles />
      <TelegramBridge />
      <UserProvider>
      <TelegramAuthGate>
      <MobileAdPopup />
      <ShelfProvider>
        <ShelfFilterProvider>
        <WishlistProvider>
          <WishlistFilterProvider>
          <RatingProvider>
              <MessagesProvider>
                <NotificationProvider>
                  <FriendsProvider>
                      <CommentsProvider>
                        <CollectionProvider>
                        <ShelfTabProvider>
                        <AchievementsProvider>
                        <FeedProvider>
                        <SearchSessionProvider>
                        <HelmetProvider>
                        <SEO />
                    <LayoutProvider>
                    <LibrarySyncStatus />
                    <AnalyticsTracker />
                    <VkAuthHandler>
                      <Suspense fallback={<AppLoader fullScreen />}>
                      <Routes>
                        {/* Публичные маршруты */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/connect" element={<AeroConnect />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/verify-email/:token" element={<VerifyEmail />} />
                        <Route path="/legal/:docId" element={<LegalDocPage />} />

                        {/* Админ-панель */}
                        <Route path="/adminchek/login" element={<AdminLogin />} />
                        <Route path="/adminchek" element={<AdminDashboard />} />

                         {/* Защищенные маршруты с Layout */}
                         <Route path="/" element={
                           <ProtectedRoute>
                             <Layout />
                           </ProtectedRoute>
                         }>
                           <Route index element={<Feed />} />
                           <Route path="search" element={<Search />} />
                           <Route path="shelf" element={<Shelf />} />
                           <Route path="wishlist" element={<Wishlist />} />
                           <Route path="collections" element={<Collections />} />
                           <Route path="collections/:id" element={<PersonalCollectionPage />} />
                           <Route path="shared-collections" element={<SharedCollections />} />
                           <Route path="shared-collections/:id" element={<SharedCollectionDetail />} />
                           <Route path="shared-collections/:id/edit" element={<SharedCollectionDetail />} />
                           <Route path="collection/:slug" element={<CollectionPage />} />
                           <Route path="profile" element={<Profile />} />
                           <Route path="profile/:username" element={<Profile />} />
                           <Route path="fandoms" element={<Fandoms />} />
                           <Route path="fandoms/new" element={<FandomEditor />} />
                           <Route path="fandom/:slug/edit" element={<FandomEditor />} />
                           <Route path="fandom/:slug" element={<FandomPage />} />
                           <Route path="friends" element={<Friends />} />
                            <Route path="notifications" element={<Notifications />} />
                            <Route path="settings" element={<Settings />} />
                           <Route path="category/:category" element={<Search />} />
                           <Route path="item/:category/:id" element={<ItemDetailRoute />} />
                           <Route path="actor/:id" element={<ActorPage />} />
                           <Route path="director/:id" element={<DirectorPage />} />
                           <Route path="company/:id" element={<CompanyPage />} />
                          </Route>

                        {/* Редирект на главную для неизвестных маршрутов */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                      </Suspense>
                    </VkAuthHandler>
                    </LayoutProvider>
                    </HelmetProvider>
                    </SearchSessionProvider>
                    </FeedProvider>
                    </AchievementsProvider>
                    </ShelfTabProvider>
                    </CollectionProvider>
                    </CommentsProvider>
                  </FriendsProvider>
              </NotificationProvider>
            </MessagesProvider>
          </RatingProvider>
          </WishlistFilterProvider>
        </WishlistProvider>
        </ShelfFilterProvider>
      </ShelfProvider>
      </TelegramAuthGate>
      </UserProvider>
    </StyledThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
